<template>
    <div class="page-wrapper container xl:max-w-[900px] mx-auto py-8 px-4 grid gap-2">
        <div class="text-center mb-6">
            <h2>Register a New Account</h2>
            <p>Enter some basic information below to get started with your new Leonard's account.</p>
        </div>

        <div class="mb-6" v-if="hasSuccess || hasError">
            <Alert type="success" title="Done!" v-if="hasSuccess">
                Registration Successful! Now attempting to sign you in...
            </Alert>
            <Alert type="danger" title="Error!" v-if="hasError">
                <span v-html="errorText"></span>
            </Alert>
        </div>

        <ValidationObserver ref="registrationForm" v-slot="{handleSubmit}" tag="div">
            <form @submit.prevent="handleSubmit(submitRegistration)" method="post">
                <div class="grid md:grid-2 md:grid-flow-col gap-2 w-full mb-3">
                    <ValidationProvider mode="eager" name="first_name" rules="required|min:2" v-slot="{errors, invalid, validated}" tag="div">
                        <label for="first_name" class="text-sm">First Name</label>
                        <input name="first_name" type="text" id="first_name" class="w-full bg-leonard-white placeholder:text-leonard-black-disabled text-leonard-black rounded lg:rounded-md p-4 border border-leonard-grey-disabled focus:outline-none focus:border-leonard-black-hover" :class="{'border-leonard-error': invalid && validated}" v-model="registration.FirstName" autofocus>
                        <p class="text-sm text-leonard-error mt-1">{{errors[0]}}</p>
                    </ValidationProvider>

                    <ValidationProvider mode="eager" name="last_name" rules="required|min:2" v-slot="{errors, invalid, validated}" tag="div">
                        <label for="last_name" class="text-sm">Last Name</label>
                        <input name="last_name" type="text" id="last_name" class="w-full bg-leonard-white placeholder:text-leonard-black-disabled text-leonard-black rounded lg:rounded-md p-4 border border-leonard-grey-disabled focus:outline-none focus:border-leonard-black-hover" :class="{'border-leonard-error': invalid && validated}" v-model="registration.LastName">
                        <p class="text-sm text-leonard-error mt-1">{{errors[0]}}</p>
                    </ValidationProvider>
                </div>

                <ValidationProvider class="flex flex-col mb-3" mode="eager" name="email" rules="required|email" v-slot="{errors, invalid, validated}" tag="div">
                    <label for="email" class="text-sm">Email Address</label>
                    <input name="email" type="text" id="email" class="w-full bg-leonard-white placeholder:text-leonard-black-disabled text-leonard-black rounded lg:rounded-md p-4 border border-leonard-grey-disabled focus:outline-none focus:border-leonard-black-hover" :class="{'border-leonard-error': invalid && validated}" v-model="registration.emailAddress">
                    <p class="text-sm italic text-leonard-disclaimer mt-1" v-if="!errors[0]">We will only use this email to send you important notifications & order receipts</p>
                    <p class="text-sm text-leonard-error mt-1">{{errors[0]}}</p>
                </ValidationProvider>

                <ValidationProvider class="flex flex-col mb-3" mode="eager" name="username" rules="required|min:2" v-slot="{errors, invalid, validated}" tag="div">
                    <label for="username" class="text-sm">Username</label>
                    <input name="username" type="text" id="username" autocomplete="username" class="w-full bg-leonard-white placeholder:text-leonard-black-disabled text-leonard-black rounded lg:rounded-md p-4 border border-leonard-grey-disabled focus:outline-none focus:border-leonard-black-hover" :class="{'border-leonard-error': invalid && validated}" v-model="registration.username">
                    <p class="text-sm italic text-leonard-disclaimer mt-1" v-if="!errors[0]">You'll use this for signing in</p>
                    <p class="text-sm text-leonard-error mt-1">{{errors[0]}}</p>
                </ValidationProvider>

                <div class="grid md:grid-2 md:grid-flow-col gap-2 w-full mb-3">
                    <ValidationProvider mode="eager" name="password" rules="required|min:8" v-slot="{errors, invalid, validated}" tag="div">
                        <label for="password" class="text-sm">Password</label>
                        <input name="password" type="password" id="password" autocomplete="new-password" class="w-full bg-leonard-white placeholder:text-leonard-black-disabled text-leonard-black rounded lg:rounded-md p-4 border border-leonard-grey-disabled focus:outline-none focus:border-leonard-black-hover" :class="{'border-leonard-error': invalid && validated}" v-model="registration.password">
                        <p class="text-sm italic text-leonard-disclaimer mt-1" v-if="!errors[0]">Must include at least one number and one symbol</p>
                        <p class="text-sm text-leonard-error mt-1">{{errors[0]}}</p>
                    </ValidationProvider>

                    <ValidationProvider mode="eager" name="password_confirmation" rules="required|min:8|confirmed:password" v-slot="{errors, invalid, validated}" tag="div">
                        <label for="password_confirmation" class="text-sm">Confirm Password</label>
                        <input name="password_confirmation" type="password" id="password_confirmation" autocomplete="new-password" class="w-full bg-leonard-white placeholder:text-leonard-black-disabled text-leonard-black rounded lg:rounded-md p-4 border border-leonard-grey-disabled focus:outline-none focus:border-leonard-black-hover" :class="{'border-leonard-error': invalid && validated}" v-model="registration.password_confirmation">
                        <span class="text-sm text-leonard-error mt-1">{{errors[0]}}</span>
                    </ValidationProvider>
                </div>

                <div class="flex justify-end mt-6">
                    <button type="submit" class="font-medium transition-all duration-300 border-2 rounded-md inline-block py-2 px-3 md:px-4 bg-leonard-green border-leonard-green text-leonard-white hover:bg-leonard-green-hover hover:border-transparent focus:ring-leonard-green cursor-pointer disabled:bg-leonard-green-disabled disabled:text-leonard-green disabled:cursor-not-allowed" :disabled="loading || hasSuccess">
                        Submit <font-awesome-icon fixed-width icon="spinner" pulse class="ml-2" v-if="loading"></font-awesome-icon>
                    </button>
                </div>
            </form>
        </ValidationObserver>
    </div>
</template>

<script>
    import { library } from '@fortawesome/fontawesome-svg-core';
    import { faSpinner } from '@fortawesome/free-solid-svg-icons';
    import {extend, ValidationObserver, ValidationProvider} from "vee-validate";
    import {confirmed, min, required, email} from "vee-validate/dist/rules";

    import Alert from "../components/app/Alert";
    import User from "../classes/User";

    extend('required', {
        ...required,
        message: (fieldName) => {
            fieldName = fieldName.toLowerCase().split('_').map(function(word) {
                return (word.charAt(0).toUpperCase() + word.slice(1));
            }).join(' ');

            return `${fieldName} is required.`;
        }
    });

    extend('confirmed', {
        ...confirmed,
        message: (fieldName) => {
            fieldName = fieldName.toLowerCase().split('_').map(function(word) {
                return (word.charAt(0).toUpperCase() + word.slice(1));
            }).join(' ');

            return `${fieldName} does not match.`;
        }
    });

    extend('email', {
        ...email,
        message: () => {
            return `Please enter a valid email address.`;
        }
    });

    extend('min', {
        ...min,
        message: (fieldName) => {
            fieldName = fieldName.toLowerCase().split('_').map(function(word) {
                return (word.charAt(0).toUpperCase() + word.slice(1));
            }).join(' ');

            return `${fieldName} must be at least 8 characters`;
        }
    });

    export default {
        data() {
            return {
                User: new User(),
                registration: {
                    FirstName: null,
                    LastName: null,
                    emailAddress: null,
                    username: null,
                    password: null,
                    password_confirmation: null,
                },
                errorText: '',
                errorsObj: {
                    FirstName: [],
                    LastName: [],
                    emailAddress: [],
                    username: [],
                    password: [],
                    password_confirmation: [],
                },
                hasError:false,
                hasSuccess:false,
                loading: false,
            }
        },

        components: {
            ValidationProvider,
            ValidationObserver,
            Alert,
        },

        created() {
            library.add(faSpinner);
        },

        beforeMount() {
            if(this.loggedIn()) this.$router.push({name: 'home'});
        },

        mounted() {
            document.querySelector('header').classList.add('solid');
            window.events.$emit('finishedLoading');
        },

        methods: {
            submitRegistration() {
                const self = this;

                this.loading = true;
                this.hasError = false;
                this.errorText = '';
                this.errorsObj = {};

                this.User.register(this.registration).then(function(response) {
                    if(!response.success) {
                        feedback('Registration Failed', 'danger');
                        self.hasError = true;
                        if(response.data.errors) {
                            self.errorText = 'There were some errors during registraion. Please try again.';
                            self.$refs.registrationForm.setErrors(response.data.errors);
                        } else {
                            self.errorText = response.message;
                        }

                        self.loading = false;
                        return;
                    }

                    self.hasSuccess = true;
                    feedback('Registered! Now logging in...', 'success');
                    self.login();
                })
            },

            login() {
                const self = this;

                this.loading = true;
                this.$auth.login({
                    data: {
                        username: self.registration.username,
                        password: self.registration.password
                    },
                    success: function(response) {
                        self.loading = false;
                        this.$router.push({name: 'home'});
                        feedback('Successfully Logged In', 'success');
                    },
                    error: function(err) {
                        self.loading = false;
                        console.error('Error signing in: ', err.response.data.message);
                        feedback('Error during sign in', 'success');
                        this.$router.push({name: 'login'});
                    },
                    rememberMe: false,
                    fetchUser: true
                });
            }
        }
    }
</script>
